import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import classes from '../../../pipeline.module.scss';
import Api from '../../../../../../../api/api';
import { Card, LineChart, SectionHead } from '../../PipelineComponents';
import { formattedCount } from '../../../pipelineHelpers';
import InterventionsLogCard from './InterventionsLogCard';

const TYPE_LABELS = {
  POLY: 'Polypharmacy (POLY)',
  ACH: 'Anticholinergic burden (ACH)',
  COB: 'COB / billing',
  DEPRESCRIBE: 'Deprescribe',
  ADHERENCE: 'Adherence counseling',
};

const TYPE_TIPS = {
  ADHERENCE: {
    text: 'Raised when a member falls more than two weeks behind on a medication.',
    steps: ['One is raised per prescription rather than per member.'],
    note: 'That is why the count runs into the tens of thousands against a few hundred members with a gap.'
      + ' Grouping by member would turn this into a workable list.',
    warn: true,
  },
  DEPRESCRIBE: {
    text: 'Raised when a prescription is cancelled.',
    steps: ['Triggered by the cancellation itself.'],
    note: 'The only type triggered by something staff do rather than by something the data shows.',
  },
  POLY: {
    text: 'Raised when a member is on five or more different medications.',
    steps: ['Cancelled prescriptions do not count toward the five.'],
    note: 'Not a problem in itself - it is a prompt to review, and most reviews end with no change.',
  },
  COB: {
    text: 'Raised when a member has two or more active insurance records.',
    steps: ['Checked against the insurance records on the member.'],
    note: 'A billing issue rather than a clinical one, but it often explains why a member\'s fills keep'
      + ' rejecting - which then shows up as a gap in section 01.',
  },
  ACH: {
    text: 'Raised when a member is on a medication with a high anticholinergic load.',
    steps: ['Checked against a known list of these medications.'],
    note: 'Usually the smallest group and the highest clinical value, particularly for older members where'
      + ' these medications carry a real risk of confusion and falls. Easy to lose under the adherence alerts.',
  },
};

const Interventions = ({ section, params }) => {
  const firstLoad = useRef(true);

  const [summary, setSummary] = useState({});
  const [loading, loadingToggle] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        loadingToggle(true);

        const { data } = await Api.getInterventionsReportSummary(params);

        setSummary(data?.data || {});

        loadingToggle(false);
        firstLoad.current = false;
      } catch (e) {
        console.warn(e);
      }
    })();
  }, [params]);

  const { total, types } = useMemo(() => {
    const kpis = summary?.kpis;

    return {
      total: kpis?.total,

      types: (summary?.byType || []).map((d) => ({
        label: TYPE_LABELS[d.type] || d.type,
        value: +(d.count || 0),
        caption: formattedCount(d.count),
        tooltip: TYPE_TIPS[d.type],
      })),

      // outcomes: [
      //   { label: 'Accepted by prescriber', value: formattedCount(kpis?.accepted) },
      //   { label: 'Pending response', value: formattedCount(kpis?.pending) },
      //   { label: 'Declined by prescriber', value: formattedCount(kpis?.declined) },
      //   { label: 'Meds stopped or reduced', value: formattedCount(kpis?.resolved) },
      //   { label: 'Acceptance rate', value: formattedPercent(kpis?.acceptanceRate) },
      // ],
    };
  }, [summary]);

  return (
    <>
      <SectionHead section={section} />

      <div className={`${classes.row} ${classes.row_2u}`}>
        <InterventionsLogCard params={params} />

        <Card
          title="Mix and outcomes"
          caption={`${formattedCount(total)} interventions in the period`}
          loading={loading}
          firstLoad={firstLoad.current}
          isEmpty={!types.length}
        >
          <LineChart
            data={types}
            className={classes.bordered}
          />

          {/* <div className={classes.kv_list}> */}
          {/*  {outcomes.map((item) => ( */}
          {/*    <div className={classes.kv} key={item.label}> */}
          {/*      {item.label} */}

          {/*      <span className={classes.kv_value}>{item.value}</span> */}
          {/*    </div> */}
          {/*  ))} */}
          {/* </div> */}
        </Card>
      </div>
    </>
  );
};

Interventions.propTypes = {
  section: PropTypes.object.isRequired,
  params: PropTypes.object.isRequired,
};

export default Interventions;
